import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, ExternalLink, X, ChevronDown, ChevronUp, ArrowLeft, XCircle, XCircleIcon, XIcon, LucideXCircle, Bookmark, BookmarkCheck } from 'lucide-react';
import { useMapStore } from '../store/mapStore';
import { STATUS } from '../types';
import CreateLeadSheet from './CreateLeadSheet';
import { openSalesCallPlan } from '../utils/sfBridge';
import FeedbackModal from "./FeedbackModal";
import Toast from './FeedbackSuccessToast';



const formatDate = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  // Check if the date is valid
  if (isNaN(date.getTime()))
    return '';

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};


// // Parse "street, city, ST ZIP[, Country]" into parts
// const parseAddressParts = (addr = '') => {
//   const parts = addr.split(',').map(s => s.trim());
//   const street = parts[0] || '';
//   const city = parts[1] || '';
//   let state = '', postalCode = '', country = '';
//   if (parts[2]) {
//     const m = parts[2].match(/([A-Za-z]{2})\s+(\d{4,10}(?:-\d{4})?)/);
//     if (m) { state = m[1]; postalCode = m[2]; } else { state = parts[2]; }
//   }
//   if (parts[3]) country = parts[3];
//   return { street, city, state, postalCode, country };
// };
// Pull domain from store; fallback to training


// Treat "target" rows specially
const isTarget = (loc) => ((loc.shipStatus ?? loc.status ?? '').toString().toLowerCase() === 'target');

const REASON_LABELS = {
  EXISTING_SYSCO_ACCOUNT: "This is an existing sysco account",
  INCORRECT_INFORMATION: "Incorrect information",
  MISSING_INFORMATION: "Missing information",
  NO_LONGER_OPERATIONAL: "This location no longer operational",
  OTHER: "Other",
};

const INCORRECT_FIELD_LABELS = {
  LOCATION_ADDRESS: "Location/Address",
  PLACE_NAME: "Place Name",
  OPERATION_HOURS: "Operation Hours",
};

const isSalesforceLead = (loc) => isTarget(loc) && String(loc.scope ?? '').trim().toUpperCase() === 'SALESFORCE';

const LocationDetails = ({ location, isMobileView = false }) => {
  //console.log('Location in LocationDetails (should be detailedLocation):', location);
  const { setSelectedLocation, isFavorite, toggleFavorite, openLeadForm, feedbackById,
    feedbackLoadingById,
    submitFeedback } = useMapStore();

  const isGoogleTarget =
    location?.shipStatus === "target" &&
    location?.scope === "GOOGLE";

  const feedbackId = location?.placeId; // or sfId if that’s what you use

  // --- stacked accounts support ---


  const stackCount = Number(location?._stackCount || 0);
  const stackMembers = Array.isArray(location?._stackMembers) ? location._stackMembers : [];
  const stackSummary = location?._stackSummary || null;
  const [isStackExpanded, setIsStackExpanded] = useState(true);
  const isStackPickerMode = stackCount > 1 && location?._stackPicked !== true;

  const getBU = (r) =>
    r?.accountDetails?.businessUnit ||
    r?.accountDetails?.business_unit ||
    r?.businessUnit ||
    r?.business_unit ||
    '';

  const normBU = (r) => (getBU(r) || '').toString().trim().toLowerCase();

  const isStack = (Number(location?._stackCount || stackMembers.length) || 0) > 1;
  const stackHasUSBL = stackMembers.some((m) => normBU(m) === 'us broadline');

  // Pick a representative row to prefill (simple + stable)
  const stackRep =
    stackMembers.find((m) => m?.accountDetails) ||
    stackMembers[0] ||
    null;

  useEffect(() => {
    // When user first opens a stacked marker, keep it expanded.
    if (stackCount > 1 && isStackPickerMode) setIsStackExpanded(true);
  }, [stackCount, isStackPickerMode]);


  // stacked + not yet picked → show picker only

  const feedbackRows = feedbackId ? feedbackById?.[feedbackId] : null;

  const isFeedbackLoading = feedbackId ? !!feedbackLoadingById?.[feedbackId] : false;

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isFeedbackSectionOpen, setIsFeedbackSectionOpen] = useState(false);

  const [toastMsg, setToastMsg] = useState("");

  const openFeedbackModal = () => {
    console.log("[feedback] open modal for", location?.placeId);
    setIsFeedbackOpen(true);
  };

  const closeFeedbackModal = () => {
    console.log("[feedback] close modal");
    setIsFeedbackOpen(false);
  };





  const handleFeedbackSubmit = async (payload) => {
    const reasonLabel = REASON_LABELS[payload.reason] || payload.reason;

    let finalComment = payload.comment?.trim() || "";

    if (payload.reason === "INCORRECT_INFORMATION" && payload.incorrectFields?.length) {
      const fieldsLabel = payload.incorrectFields
        .map((k) => INCORRECT_FIELD_LABELS[k] || k)
        .join(", ");

      finalComment = `Incorrect fields: ${fieldsLabel}\n\n${finalComment}`.trim();
    }

    try {
      await submitFeedback({
        id: location?.placeId,
        name: location?.name,
        scope: "GOOGLE",
        // keep backend reason clean too (no ALL CAPS)
        reason: reasonLabel,
        comment: finalComment,
      });

      setToastMsg("Feedback submitted");
      closeFeedbackModal();
    } catch (e) {
      console.error("[feedback] post failed", e);
    }
  };
  // const isSalesforceLead = (location.shipStatus?.toLowerCase() === 'target' || location.status?.toLowerCase() === 'target') && location.scope === 'SALESFORCE';

  // const getStatusBadgeClass = () => {
  //   switch (location.status?.toLowerCase()) {
  //     case 'active':
  //       return 'bg-green-100 text-green-800';
  //     case 'inactive':
  //       return 'bg-yellow-100 text-yellow-800';
  //     case 'prospect':
  //       return 'bg-blue-100 text-blue-800';
  //   }
  // };

  // const openInGoogleMaps = () => {
  //   const url = `https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lng}`;
  //   window.open(url, '_blank');
  // };

  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location.address || `${location.lat},${location.lng}`)
    }`;

  // Google Business Search URL
  const googleBusinessSearchUrl = `https://www.google.com/search?q=${encodeURIComponent((location.name || '') + ' ' + (location.address || ''))}`;

  // const openInSalesforce = () => {
  //   if (location.accountNumber) {
  //     // This would normally open the Salesforce record
  //     alert('This would open the Salesforce record in a new tab');
  //   }
  // };


  // const renderHeader = () => (
  //   <div className="flex items-start justify-between p-4 border-b border-gray-200">
  //     <div className="flex-1">
  //       <div className="flex items-center space-x-2">
  //         <h2 className="text-fluid-lg font-semibold">{location.name}</h2>
  //         <span className={`px-2 py-1 text-fluid-xs rounded-full ${getStatusBadgeClass()}`}>
  //           {location.status}
  //         </span>
  //       </div>
  //       <div className="mt-1 flex items-center text-fluid-sm text-gray-500">
  //         {/* <img src="/assets/perks-icon.svg" alt="Perks" className="h-13 w-12 mr-3" /> */}
  //         <span>{location.accountDetails.accountId || ''}</span>
  //         <span className="mx-2">|</span>
  //         <span>{location.accountDetails?.type || ''}</span>
  //         <span className="mx-2">|</span>
  //         <span> {location.status}</span>
  //         <span className="mx-2"> </span>
  //         {/* CSS - Sky blue pill with white text */}
  //         {location.customerSegmentation && (
  //           <span className="px-2 py-1 text-fluid-xs rounded-full bg-sky-500 text-white ml-2 whitespace-nowrap">
  //             CSS:{location.customerSegmentation}
  //           </span>
  //         )}

  //         {/* Perks pill – green only when enrolled, gray otherwise */}
  //         {location.accountDetails?.perks?.toLowerCase() === 'enrolled' && (
  //           <span className="px-2 py-1 text-fluid-xs rounded-full bg-green-600 text-white ml-2">
  //             Perks
  //           </span>
  //         )}
  //         {location.accountDetails?.yourWay?.toLowerCase() === 'enrolled' && (
  //           <span className="px-2 py-1 text-fluid-xs rounded-full bg-green-600 text-white ml-2">
  //             SYW
  //           </span>
  //         )}

  //       </div>
  //     </div>
  //     {!isMobileView && (
  //       <button
  //         onClick={() => setSelectedLocation(null)}
  //         className="p-1 hover:bg-gray-100 rounded-full transition-colors"
  //       >
  //         <X className="h-5 w-5" />
  //       </button>
  //     )}
  //   </div>
  // );



  const renderStackPicker = () => {
    if (stackCount <= 1) return null;

    return (
      <div className="px-4 pt-3 pb-3 border-b border-gray-200">
        <div
          className="flex items-center justify-between cursor-pointer select-none"
          onClick={() => setIsStackExpanded((v) => !v)}
        >
          <div className="font-semibold text-sm">Stacked accounts ({stackCount})</div>
          <div className="text-xs text-gray-600 flex items-center gap-2">
            <span>{isStackExpanded ? 'Hide' : 'Show'}</span>
            <span>{isStackExpanded ? '▾' : '▸'}</span>
          </div>
        </div>

        {isStackExpanded && (<div className="mt-2 space-y-2">
          {stackMembers.map((m, idx) => {
            const displayName = m?.name ?? m?.companyName ?? 'Unnamed';
            const displayAddr = m?.address ?? '';
            const statusRaw = (m?.status ?? '').toString();

            const businessUnit =
              m?.accountDetails?.business_unit ||
              m?.accountDetails?.businessUnit ||
              m?.businessUnit ||
              m?.business_unit ||
              '';

            return (
              <button
                key={m?.placeId ?? m?.id ?? `${m?.lat},${m?.lng}-${idx}`}
                type="button"
                className="w-full text-left px-3 py-2 rounded-md border border-gray-200 bg-white hover:bg-gray-50"
                onClick={() => {
                  console.log('[LocationDetails] stack member chosen:', {
                    name: displayName,
                    id: m?.id,
                    placeId: m?.placeId,
                  });

                  // Preserve stack context + mark picked
                  setSelectedLocation({
                    ...m,
                    _stackCount: stackCount,
                    _stackMembers: stackMembers,
                    _stackSummary: stackSummary,
                    _stackPicked: true,
                    _skipRecenter: true,
                  });
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-sm truncate">{displayName}</div>
                  <div className="text-xs text-gray-600 flex-shrink-0">
                    {businessUnit ? businessUnit : ''}
                    {businessUnit && statusRaw ? ' • ' : ''}
                    {statusRaw ? statusRaw : ''}
                  </div>
                </div>

                {displayAddr ? (
                  <div className="text-xs text-gray-600 mt-0.5 truncate">{displayAddr}</div>
                ) : null}
              </button>
            );
          })}
        </div>)}
      </div>
    );
  };



  const renderHeaderMobile = () => (
    <div className="flex items-center justify-between px-4 pt-3 pb-3 border-b border-gray-200">
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <h2 className="text-fluid-lg text-zinc-700 font-bold">{location.name}</h2>
          <div className="flex items-center gap-1">
            {/* Favorite toggle */}
            <button
              type="button"
              className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-pressed={isFavorite(location)}
              aria-label={isFavorite(location) ? 'Remove from favorites' : 'Save to favorites'}
              onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
              onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
              onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); }}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleFavorite(location); }}
            >
              {isFavorite(location)
                ? <BookmarkCheck className="h-5 w-5 text-blue-600" />
                : <Bookmark className="h-5 w-5 text-gray-600" />
              }
            </button>
            {/* Close */}
            <button
              onClick={() => setSelectedLocation(null)}
              className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <LucideXCircle className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

        <div className="mt-1 flex items-center text-fluid-sm text-gray-500">
          {/* <img src="/assets/perks-icon.svg" alt="Perks" className="h-13 w-12 mr-3" /> */}
          <span>{location.accountDetails.accountId || ''}</span>
          <span className="mx-2">|</span>
          <span>{location.accountDetails?.type || ''}</span>
          <span className="mx-2">|</span>
          <span> {location.status}</span>
          <span className="mx-2"> </span>
          {/* CSS - Sky blue pill with white text */}
          {location.customerSegmentation && (
            <span className="px-2 py-1 text-fluid-xs rounded-full bg-sky-500 text-white ml-2 whitespace-nowrap">
              CSS:{location.customerSegmentation}
            </span>
          )}

          {/* Perks pill – green only when enrolled, gray otherwise */}
          {location.accountDetails?.perks?.toLowerCase() === 'enrolled' && (
            <span className="px-2 py-1 text-fluid-xs rounded-full bg-green-600 text-white ml-2">
              Perks
            </span>
          )}

          {location.accountDetails?.yourWay?.toLowerCase() === 'enrolled' && (
            <span className="px-2 py-1 text-fluid-xs rounded-full bg-green-600 text-white ml-2">
              SYW
            </span>
          )}

        </div>
      </div>
    </div>
  );

  const renderTargetHeaderMobile = () => (

    <div className="flex items-center justify-between px-4 pt-3 pb-3 border-b border-gray-200">
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <h2 className="text-fluid-lg text-zinc-700 font-bold">{location.name}</h2>
          <div className="flex items-center gap-1">
            {/* Favorite toggle */}
            <button
              type="button"
              className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-pressed={isFavorite(location)}
              aria-label={isFavorite(location) ? 'Remove from favorites' : 'Save to favorites'}
              onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
              onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
              onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); }}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleFavorite(location); }}
            >
              {isFavorite(location)
                ? <BookmarkCheck className="h-5 w-5 text-blue-600" />
                : <Bookmark className="h-5 w-5 text-gray-600" />
              }
            </button>
            {/* Close */}
            <button
              onClick={() => setSelectedLocation(null)}
              className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <LucideXCircle className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>


        <div className="mt-1 flex items-center text-fluid-sm text-gray-500">
          <span>Target</span>
          {isSalesforceLead(location) && (
            <>
              <span className="mx-2">|</span>
              <span>Lead</span>
            </>
          )}

          {location.status && (
            <>
              <span className="mx-2">|</span>
              <span>{location.status}</span>
            </>
          )}
          {!isSalesforceLead(location) && location.contactInfo?.currentHours && (
            <>
              <span className="mx-2">|</span>
              <span className={`text-fluid-xs px-2 py-1 rounded-full font-medium ${location.contactInfo.currentHours.open_now ? 'bg-green-600 text-white ml-2' : 'bg-red-600 text-white ml-2'
                }`}>
                {location.contactInfo.currentHours.open_now ? 'Open Now' : 'Closed'}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // const renderTargetHeader = () => (
  //   <div className="flex items-start justify-between p-4 border-b border-gray-200">
  //     <div className="flex-1">
  //       <h2>{location.name}</h2>
  //     </div>
  //     <button onClick={() => setSelectedLocation(null)} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
  //       <X className="h-5 w-5" />
  //     </button>
  //   </div>
  // );


  const getSalesforceBase = () => {
    try {
      const b = useMapStore.getState().salesforceBaseUrl;
      return (b && b.trim()) ? b.replace(/\/+$/, '') : 'https://sysco--training.sandbox.lightning.force.com';
    } catch {
      return 'https://sysco--training.sandbox.lightning.force.com';
    }
  };

  const SYSCO360_RECORD_TYPE_ID = '012E0000000V4MQIA0'; // your Sysco360 Lead RTId
  const SALES_MAP_TAB = '%2Flightning%2Fn%2FseSalesMap';


  const buildScpUrl = (recordid) => {
    const scp_full_url = getSalesforceBase() + '/lightning/action/quick/Account.Create_Call_Plans?' +
      'recordId=' + recordid +
      '&backgroundContext=' + SALES_MAP_TAB +
      '&retURL=' + SALES_MAP_TAB
    console.log("Info: Sales call Plan full URL: ", scp_full_url)
    return scp_full_url
  };

  // helper: open SCP in the parent (breaks out of iframe)
  // const openScpInParent = (url) => {
  //   try {
  //     // if we're inside an iframe, go to the parent; else use current window
  //     const dest = (window.parent && window.parent !== window) ? window.parent : window;
  //     dest.location.assign(url);
  //   } catch {
  //     window.location.href = url;
  //   }
  // };

  const handleSalesCallPlan = (e, accountId) => {
    e?.preventDefault?.();
    if (!accountId) {
      console.warn('[LocationDetails] Missing accountId for Sales Call Plan.', { location });
      return;
    }
    openSalesCallPlan(accountId);
  };

  // const buildScpMobileUrl = (recordid) => {
  //   const scp_full_url = getSalesforceBase() + '/lightning/action/quick/Account.Create_Call_Plan_Mobile?' +
  //     'recordId=' + recordid +
  //     '&backgroundContext=' + SALES_MAP_TAB +
  //     '&retURL=' + SALES_MAP_TAB
  //   console.log("Info: Sales call Plan full URL: ", scp_full_url)
  //   return scp_full_url
  // };


  // Build Create Lead URL with defaultFieldValues from the location
  // const buildCreateLeadUrl = (location) => {

  //   const CREATE_LEAD_BASE = getSalesforceBase() + '/lightning/o/lead/new' +
  //     '?recordTypeId=' + SYSCO360_RECORD_TYPE_ID +
  //     '&nooverride=1' +
  //     '&backgroundContext=' + SALES_MAP_TAB +
  //     '&retURL=' + SALES_MAP_TAB

  //   const { street, city, state, postalCode, country } = parseAddressParts(location.address || '');
  //   const company = location.accountDetails?.accountName || location.name || '';

  //   const defaults = {
  //     Company: company,
  //     Street: street,
  //     City: city,
  //     State: state,
  //     PostalCode: postalCode,
  //     Country: country,
  //     // LeadSource: 'Sales Map', // optional
  //   };

  //   const kv = Object.entries(defaults)
  //     .filter(([, v]) => String(v || '').trim() !== '')
  //     .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
  //     .join(',');

  //   let final_lead_url = kv ? `${CREATE_LEAD_BASE}&defaultFieldValues=${kv}` : CREATE_LEAD_BASE;


  //   console.log("Info: create lead url: ", final_lead_url)

  //   return final_lead_url;
  // };
  // const goSameTab = (url) => {
  //   try { sessionStorage.setItem('salesmap_refresh_needed', '1'); } catch { }
  //   window.top.location.href = url;
  // };

  // const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

  const renderActions = () => {
    const isWeb = !isMobileView;
    // console.log('Render Actions: isWeb=', isWeb, ', isMobileView=', isMobileView, ', location=', location);

    if (location?.userAccount) {
      if (isWeb) {
        // --- Web: keep external link redirect using buildScpUrl ---
        const scpUrl = buildScpUrl(location.id);
        return (
          <div className="p-3 flex border-gray-200">
            <a
              href={scpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-pill btn-pill-xs pill-blue-sel ml-auto flex items-center justify-center"
              onClick={() => console.log('[action] Sales Call Plan (web) for', location.name)}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Create Sales Call Plan
            </a>
          </div>
        );
      } else {
        // --- Mobile (Salesforce Canvas): use postMessage bridge ---
        return (
          <div className="p-3 flex border-gray-200">
            <button
              type="button"
              className="btn-pill btn-pill-xs pill-blue-sel ml-auto flex items-center justify-center"
              onClick={(e) => handleSalesCallPlan(e, location.id)}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Create Sales Call Plan
            </button>
          </div>
        );
      }
    }



    //  --- Create Lead (only for targets that are NOT Salesforce leads)
    const isLead = isSalesforceLead(location); // you already define this earlier in the file

    if (isStack && !stackHasUSBL && stackRep) {
      return (
        <div className="p-3 flex border-t border-gray-200">
          <button
            type="button"
            className="btn-pill btn-pill-s pill-blue-sel ml-auto flex items-center justify-end"
            onClick={() => {
              console.log('[action] Create USBL Lead (stack)', {
                stackCount: location?._stackCount,
                repName: stackRep?.name,
              });
              openLeadForm(stackRep);
            }}
          >
            Create USBL Lead
          </button>
        </div>
      );
    }

    // --- Create Lead for targets that are NOT Salesforce leads
    if (isTarget(location) && !isLead) {

      return (
        <div className="p-3 flex border-gray-200">
          <button
            type="button"
            className="btn-pill btn-pill-s pill-blue-sel ml-auto flex items-center justify-end"
            onClick={() => { console.log('[action] open create lead form for', location.name); openLeadForm(location); }}
          >
            Create Lead
          </button>

          {isGoogleTarget && (
            <button
              type="button"
              className="btn-pill btn-pill-s pill-blue-sel flex items-center justify-end ml-2"
              onClick={() => {
                console.log("[feedback] open modal for", location.placeId); openFeedbackModal();
              }}
            >
              Give Feedback
            </button>
          )}
        </div>
      );
    }
    // --- Create Lead (web) for targets that are NOT Salesforce leads

    // if (isWeb && isTarget(location) && !isLead) {
    //   const leadUrl = buildCreateLeadUrl(location);
    //   return (
    //       <div className="p-3 flex border-gray-200">
    //       <a
    //         href={leadUrl}
    //         className="btn-pill btn-pill-xs pill-blue-sel ml-auto flex items-center justify-center"
    //         target="_blank"
    //         rel="noopener noreferrer" // mobile: new tab
    //         // onClick= {(e) => { if (isAndroid) {
    //         //                   try { sessionStorage.setItem('salesmap_refresh_needed', '1'); } catch {}
    //         //                   window.top.location.replace(leadUrl);
    //         //                 }
    //         //   // else let normal <a> handle it → new tab
    //         // }}
    //         onClick={() => console.log('[action] create lead for', location.name)}

    //       >
    //         <ExternalLink className="h-4 w-4 mr-2" />
    //         Create Lead
    //       </a>
    //     </div>
    //   );
    // }




    return null;
  };




  const renderAccountDetails = () => (
    <div className="p-3">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-fluid-sm text-gray-500">Account Owner:</p>
          <p className="text-fluid-sm font-bold">{location.contactInfo.ownerName}</p>
        </div>
        <div>
          <p className="text-fluid-sm text-gray-500">Rolling 6-wk Avg Sales:</p>
          <p className="text-fluid-sm font-bold">$ {location.accountDetails?.avgSales ?? '0.0'}</p>
        </div>
        <div>
          <p className="text-fluid-sm text-gray-500">Business Unit:</p>
          <p className="text-fluid-sm font-bold">{location?.accountDetails?.businessUnit ?? ''}</p>
        </div>
      </div>
      <div className="pt-3 pb-3">
        <div>
          <p className="text-fluid-sm text-gray-500">Shipping Address:</p>
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sky-600 hover:underline text-fluid-sm font-bold" >
            <MapPin className="h-4 w-4 shrink-0" />
            {location.address}
          </a>
          {/* Google Business Search Link */}
          <div className="mt-1">
            <a
              href={googleBusinessSearchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 pt-1 text-sky-600 hover:underline text-fluid-sm font-bold"
            >
              <ExternalLink className="h-4 w-4 shrink-0" />
              Find Business on Google
            </a>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">

      <div>
        <p className="text-fluid-sm text-gray-500">Primary Menu Type:</p>
        <p className="text-fluid-sm font-bold">{location?.accountDetails?.cuisine ?? ''}</p>
      </div>
      <div>
        <p className="text-fluid-sm text-gray-500">Industry:</p>
        <p className="text-fluid-sm font-bold">{location?.industry ?? ''}</p>
      </div>
      </div>
    </div>
  );

  const renderTargetDetails = () => {
    const phone = location.contactInfo?.formattedPhoneNum;
    const website = location.contactInfo?.website;
    const placeTypes = location.placeType?.slice(0, 2) || [];
    const currentHours = location.contactInfo?.currentHours;
    return (
      <div className="p-3">
        {isSalesforceLead(location) ? (
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-fluid-sm text-gray-500">Address:</p>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sky-600 hover:underline text-fluid-sm font-bold" >
                <MapPin className="h-4 w-4 shrink-0" />
                {location.address}
              </a>
              {/* Google Business Search Link for Target */}
              <div className="pt-2 pb-2">
                <a
                  href={googleBusinessSearchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sky-600 hover:underline text-fluid-sm font-bold"
                >
                  <ExternalLink className="h-4 w-4 shrink-0" />
                  Find Business on Google
                </a>
              </div>
            </div>
            <div>
              <p className="text-fluid-sm text-gray-500">Type:</p>
              <p className="font-medium">{location.type || 'N/A'}</p>
            </div>
            <div>
              <p className="text-fluid-sm text-gray-500">Source:</p>
              <p className="font-medium">{location.source || 'N/A'}</p>
            </div>
            <div>
              <p className="text-fluid-sm text-gray-500">Assigned DSM:</p>
              <p className="font-medium">{location.assignDsmName || 'N/A'}</p>
            </div>
            <div>
              <p className="text-fluid-sm text-gray-500">Created Date:</p>
              <p className="font-medium">{formatDate(location.createdDate)}</p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-fluid-sm text-gray-500 pb-2">Address:</p>
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sky-600 hover:underline text-fluid-sm font-bold" >
                  <MapPin className="h-4 w-4 shrink-0" />
                  {location.address}
                </a>
                {/* Google Business Search Link for Target */}
                <div className="pt-2 pb-2">
                  <a
                    href={googleBusinessSearchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sky-600 hover:underline text-fluid-sm font-bold"
                  >
                    <ExternalLink className="h-4 w-4 shrink-0" />
                    Find Business on Google
                  </a>
                </div>
              </div>
              <div>
                <p className="text-fluid-sm text-gray-500 pb-2">Type:</p>
                {placeTypes.map((type, index) => (
                  <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-fluid-xs">
                    {type}
                  </span>
                ))}
              </div>


              {website && (
                <div>
                  <p className="text-fluid-sm text-gray-500 pb-2">Website:</p>
                  <a href={website} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline break-all">
                    {website}
                  </a>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                {phone && (
                  <div>
                    <p className="text-fluid-sm text-gray-500 pb-2">Phone:</p>
                    {isMobileView ? (
                      <a href={`tel:${phone}`} className="text-blue-600 underline">{phone}</a>
                    ) : (
                      <span className="text-gray-800">{phone}</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {currentHours && (
              <div>
                <p className="pt-3 pb-2 text-fluid-sm text-gray-500">Current Hours:</p>
                <div className="flex flex-wrap gap-2">
                  {currentHours.weekday_text?.map((hour, index) => (
                    <span key={index} className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-fluid-xs">
                      {hour}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {isGoogleTarget && (
              <div className="mt-3">
                <button
                  type="button"
                  className="w-full flex items-center justify-between py-2 text-fluid-sm text-gray-500"
                  onClick={() => setIsFeedbackSectionOpen(v => !v)}
                >
                  <span>Feedback ({feedbackRows?.length || 0})</span>

                  {isFeedbackSectionOpen ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </button>

                {isFeedbackSectionOpen && (
                  <div className="mt-2 space-y-2">
                    {isFeedbackLoading && (
                      <div className="text-sm opacity-70">Loading feedback…</div>
                    )}

                    {Array.isArray(feedbackRows) && feedbackRows.length > 0 ? (
                      feedbackRows.map((fb, idx) => (
                        <div
                          key={`${fb.createdAt || "na"}-${idx}`}
                          className="p-2 border rounded text-sm"
                        >
                          <div>
                            <span className="font-semibold">
                              {fb.userName || "Unknown"}
                            </span>
                            {" · "}
                            {fb.createdAt
                              ? new Date(fb.createdAt).toLocaleString()
                              : ""}
                          </div>

                          <div className="mt-1">
                            <b>Reason:</b> {fb.reason}
                          </div>

                          {fb.comment && (
                            <div className="mt-1">
                              <b>Comment:</b> {fb.comment}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-sm opacity-60">No feedback yet.</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };




  // const renderContactInfo = () => (
  //   <div className="p-3">
  //     <div>
  //       <p className="text-fluid-sm text-gray-500">Shipping Address:</p>
  //       <p className="font-medium">{location.address}</p>
  //     </div>
  //   </div>
  // );

  const renderRecentActivity = () => (
    <div className="p-3">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <p className="text-fluid-sm text-gray-500"> Last Call Plan:</p>
          <p className="text-fluid-sm font-bold">{location?.accountDetails?.lastCallPlan ? new Date(location.accountDetails.lastCallPlan + 'T00:00:00').toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }) : ''}</p>
        </div>
        <div>
          <p className="text-fluid-sm text-gray-500">Created Date:</p>
          <p className="text-fluid-sm font-bold">{formatDate(location.accountDetails?.createdDate || '')}</p>
        </div>
        <div>
          <p className="text-fluid-sm text-gray-500">Last Ship Date:</p>
          <p className="text-fluid-sm font-bold">
            {location.accountDetails?.lastSale ? new Date(location.accountDetails.lastSale + 'T00:00:00').toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            }) : ''}

          </p>
        </div>
      </div>
    </div>
  );

  if (isMobileView) {
    return (

      <div className="fixed inset-0 bg-white z-50">
        {renderStackPicker()}
        {(location.shipStatus || location.status) === 'target' ? renderTargetHeaderMobile() : renderHeaderMobile()}
        {renderActions()}

        {!isStackPickerMode && (
          <>
            {location.shipStatus === 'target' ? (
              renderTargetDetails()
            ) : (
              <>
                {renderAccountDetails()}
                {/* {renderContactInfo()} */}
                {renderRecentActivity()}
              </>
            )}
          </>
        )}
        <div className="flex-1 overflow-y-auto pb-24" style={{ height: 'calc(100vh - 20rem)' }}>
          {(location.shipStatus || location.status) === 'target' ? (
            renderTargetDetails()
          ) : (
            <>
              {renderAccountDetails()}
              {/* {renderContactInfo()} */}
              {renderRecentActivity()}
            </>
          )}
        </div>
        {/* Lead Form Sheet (mobile) */}
        <CreateLeadSheet />
        <FeedbackModal
          isOpen={isFeedbackOpen}
          onClose={closeFeedbackModal}
          onSubmit={handleFeedbackSubmit}
          isMobileView={isMobileView}
        />
        <Toast
          open={!!toastMsg}
          message={toastMsg}
          onClose={() => setToastMsg("")}
        />

      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg overflow-y-auto max-h-[550px] ">

      {renderStackPicker()}

      {location.shipStatus === 'target' ? renderTargetHeaderMobile() : renderHeaderMobile()}

      {renderActions()}

      {!isStackPickerMode && (
        <>
          {location.shipStatus === 'target' ? (
            renderTargetDetails()
          ) : (
            <>
              {renderAccountDetails()}
              {/* {renderContactInfo()} */}
              {renderRecentActivity()}
            </>
          )}
        </>
      )}
      <FeedbackModal
        isOpen={isFeedbackOpen}
        onClose={closeFeedbackModal}
        onSubmit={handleFeedbackSubmit}
        isMobileView={isMobileView}
      />
      <Toast
        open={!!toastMsg}
        message={toastMsg}
        onClose={() => setToastMsg("")}
      />
    </div>

  );
};

export default LocationDetails;